import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import { 
  Users, 
  Search, 
  Filter,
  MapPin,
  Briefcase,
  Clock,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Eye,
  MessageSquare,
  ShieldCheck,
  Globe,
  Award,
  Lock,
  Send,
  X,
  FileText,
  GraduationCap
} from 'lucide-react';
import { employer } from '../../api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmployerTalentPool = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ professions: [], countries: [] });
  
  // Filter state
  const [search, setSearch] = useState('');
  const [professionFilter, setProfessionFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [minExperience, setMinExperience] = useState('');
  const [page, setPage] = useState(1);
  const limit = 12;
  
  // Contact request modal
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [contactMessage, setContactMessage] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');
    
    if (!token || userType !== 'employer') {
      navigate('/login');
      return;
    }
    
    fetchStats();
    fetchEntries();
  }, [professionFilter, countryFilter, minExperience, page]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/employers/talent-pool-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      let url = `${API_URL}/api/employers/talent-pool?skip=${(page - 1) * limit}&limit=${limit}`;
      
      if (professionFilter && professionFilter !== 'all') url += `&profession=${encodeURIComponent(professionFilter)}`;
      if (countryFilter && countryFilter !== 'all') url += `&country=${encodeURIComponent(countryFilter)}`;
      if (minExperience) url += `&min_experience=${minExperience}`;
      if (search) url += `&specialisation=${encodeURIComponent(search)}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch entries');
      
      const data = await response.json();
      setEntries(data.entries);
      setTotal(data.total);
      setFilters(data.filters);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast({
        title: 'Error',
        description: 'Failed to load talent pool',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEntries();
  };

  const openContactModal = (candidate) => {
    setSelectedCandidate(candidate);
    setContactMessage('');
    setShowContactModal(true);
  };

  const sendContactRequest = async () => {
    if (!selectedCandidate) return;
    
    try {
      setSendingRequest(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(
        `${API_URL}/api/employers/talent-pool/${selectedCandidate.id}/request-contact?message=${encodeURIComponent(contactMessage)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error('Failed to send request');
      
      const data = await response.json();
      
      toast({
        title: 'Request Sent',
        description: data.message,
      });
      
      setShowContactModal(false);
      setSelectedCandidate(null);
      setContactMessage('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send contact request',
        variant: 'destructive'
      });
    } finally {
      setSendingRequest(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Talent Pool</h1>
                <p className="text-gray-600 mt-1">Browse medical professionals who have joined our talent network</p>
              </div>
              <Link to="/employer/dashboard">
                <Button variant="outline">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-teal-600 font-medium">Available Candidates</p>
                      <p className="text-3xl font-bold text-teal-700">{stats.total_candidates}</p>
                    </div>
                    <Users className="h-10 w-10 text-teal-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Avg. Experience</p>
                      <p className="text-3xl font-bold text-blue-700">{stats.average_experience} yrs</p>
                    </div>
                    <Award className="h-10 w-10 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Top Profession</p>
                      <p className="text-lg font-bold text-purple-700 truncate">
                        {stats.by_profession[0]?.name || 'N/A'}
                      </p>
                    </div>
                    <Briefcase className="h-10 w-10 text-purple-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-600 font-medium">Countries</p>
                      <p className="text-3xl font-bold text-amber-700">{stats.by_country?.length || 0}</p>
                    </div>
                    <Globe className="h-10 w-10 text-amber-400" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Info Banner */}
          <Card className="mb-6 border-2 border-teal-200 bg-teal-50">
            <CardContent className="p-4 flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="h-6 w-6 text-teal-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-teal-900">Profile Reviewed Candidates</h3>
                <p className="text-sm text-teal-700">
                  Candidates in our talent pool have submitted their CVs for review. Contact details are protected 
                  — request to connect through our platform. Note: Profile review checks for completeness, not regulatory approval.
                </p>
              </div>
              <Lock className="h-5 w-5 text-teal-400" />
            </CardContent>
          </Card>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Search Specialisation</label>
                  <form onSubmit={handleSearch} className="flex space-x-2">
                    <Input
                      placeholder="e.g., Critical Care, Emergency..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="flex-1"
                    />
                    <Button type="submit" variant="outline">
                      <Search className="h-4 w-4" />
                    </Button>
                  </form>
                </div>
                
                <div className="w-48">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Profession</label>
                  <Select value={professionFilter} onValueChange={(v) => { setProfessionFilter(v); setPage(1); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Professions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Professions</SelectItem>
                      {filters.professions.filter(p => p && p.trim()).map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-44">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Country</label>
                  <Select value={countryFilter} onValueChange={(v) => { setCountryFilter(v); setPage(1); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {filters.countries.filter(c => c && c.trim()).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-36">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Min. Experience</label>
                  <Select value={minExperience || "any"} onValueChange={(v) => { setMinExperience(v === "any" ? "" : v); setPage(1); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="2">2+ years</SelectItem>
                      <SelectItem value="5">5+ years</SelectItem>
                      <SelectItem value="10">10+ years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
            </div>
          ) : entries.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No candidates found matching your criteria</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    setProfessionFilter('all');
                    setCountryFilter('all');
                    setMinExperience('');
                    setSearch('');
                    setPage(1);
                  }}
                >
                  Clear Filters
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {entries.map((candidate) => (
                  <Card key={candidate.id} className="border-2 border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                            {candidate.first_name}{candidate.last_name.charAt(0)}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {candidate.first_name} {candidate.last_name}
                            </h3>
                            <p className="text-sm text-gray-500">{candidate.specialisation}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          Reviewed
                        </Badge>
                      </div>
                      
                      {/* Details */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Briefcase className="h-4 w-4 mr-2 text-gray-400" />
                          {candidate.profession}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                          {candidate.country}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock className="h-4 w-4 mr-2 text-gray-400" />
                          {candidate.years_experience} years experience
                        </div>
                      </div>
                      
                      {/* Contact Info Hidden Notice */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-4 text-center">
                        <div className="flex items-center justify-center text-gray-400 text-sm">
                          <Lock className="h-4 w-4 mr-2" />
                          Contact details protected
                        </div>
                      </div>
                      
                      {/* Action Button */}
                      <Button 
                        className="w-full bg-teal-600 hover:bg-teal-700"
                        onClick={() => openContactModal(candidate)}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Request to Connect
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} candidates
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Contact Request Modal */}
      {showContactModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Request to Connect</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowContactModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white font-bold">
                  {selectedCandidate.first_name}{selectedCandidate.last_name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold">{selectedCandidate.first_name} {selectedCandidate.last_name}</p>
                  <p className="text-sm text-gray-500">{selectedCandidate.profession} • {selectedCandidate.specialisation}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Message (Optional)
                </label>
                <Textarea
                  placeholder="Introduce yourself and explain why you'd like to connect with this candidate..."
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  rows={4}
                />
              </div>
              
              <div className="bg-teal-50 p-3 rounded-lg text-sm text-teal-700">
                <p className="font-medium mb-1">What happens next?</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Our team reviews your request within 24-48 hours</li>
                  <li>We contact the candidate on your behalf</li>
                  <li>If interested, we facilitate the introduction</li>
                </ul>
              </div>
              
              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowContactModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  onClick={sendContactRequest}
                  disabled={sendingRequest}
                >
                  {sendingRequest ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Request
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default EmployerTalentPool;
